import type { AppointmentTypeDto } from '@shared/core/appointment-types'
import type {
  AccountCancellationPolicyDto,
  CancellationPolicyFieldsDto,
  CancellationPolicyMode,
} from '@shared/core/cancellation-policy'
import { formatPolicyWindow } from './cancellation-policy-form.util'

export const TYPE_POLICY_OFF_SUMMARY = 'Off · customers can cancel at any time'

type PolicyRules = Pick<
  CancellationPolicyFieldsDto,
  'notice_value' | 'notice_unit' | 'allow_late_cancel' | 'allow_reschedule_in_window'
>

export type TypePolicySummaryInput = {
  mode: CancellationPolicyMode
  account: AccountCancellationPolicyDto | undefined
  /** The custom fields as typed; null while the window is not a valid number. */
  custom: PolicyRules | null
  /** The type's own reschedule and the account's are both on. */
  reschedulePossible: boolean
}

function describeRules(rules: PolicyRules, reschedulePossible: boolean): string {
  const lateCancels = rules.allow_late_cancel ? 'late cancels allowed' : 'late cancels blocked'
  const suffix = reschedulePossible && !rules.allow_reschedule_in_window ? ', reschedules blocked' : ''
  return `cancel ${formatPolicyWindow(rules.notice_value, rules.notice_unit)} ahead, ${lateCancels}${suffix}`
}

export function describeTypePolicySummary({
  mode,
  account,
  custom,
  reschedulePossible,
}: TypePolicySummaryInput): string {
  if (mode === 'custom') return custom ? `Custom · ${describeRules(custom, reschedulePossible)}` : 'Custom'
  if (mode === 'none') return TYPE_POLICY_OFF_SUMMARY
  if (!account) return 'Account policy'
  if (!account.enabled) return TYPE_POLICY_OFF_SUMMARY
  return `Account policy · ${describeRules(account, reschedulePossible)}`
}

/** R18 list badge, from the public type read: no effective policy reads "None" whatever the mode. */
export function describeTypePolicyBadge(
  type: Pick<AppointmentTypeDto, 'cancellation_policy_mode' | 'effective_cancellation_policy'>,
): string {
  const effective = type.effective_cancellation_policy
  if (!effective || type.cancellation_policy_mode === 'none') return 'None'
  if (type.cancellation_policy_mode === 'custom') return `Custom: ${effective.window_value}${effective.window_unit[0]}`
  return 'Account'
}
